import {
  HttpError,
  useApiUrl,
  useCustom,
  useCustomMutation,
} from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { useSession } from "hooks/useSession";
import { is404 } from "utils/network";
import type {
  CompleteOnboardingQuestionnaireRequest,
  CreatorProfile,
  OnboardingQuestionnaire,
  OnboardingQuestionnaireType,
} from "../types";

/**
 * Loads the onboarding questionnaire template for the current organization and
 * submits the filled template. The submit POST returns the created/updated
 * org-scoped Creator Profile; on success we invalidate any resolved
 * creator-profiles queries so gating/settings reflect the new profile.
 */
export const useOnboardingQuestionnaire = (
  type: OnboardingQuestionnaireType
) => {
  const apiUrl = useApiUrl();
  const queryClient = useQueryClient();
  const { currentOrganization } = useSession();
  const organizationId = currentOrganization?.id;

  const baseUrl = `${apiUrl}/media/${organizationId}/onboarding-questionnaire`;

  const { data, isLoading, isError, error } = useCustom<
    OnboardingQuestionnaire,
    HttpError
  >({
    url: baseUrl,
    method: "get",
    config: { query: { type } },
    errorNotification: false,
    queryOptions: {
      enabled: !!organizationId,
      // The template is static within a session; cache it so navigating
      // between wizard steps doesn't refetch on every mount.
      staleTime: 5 * 60 * 1000,
      retry: (count, err) => !is404(err) && count < 2,
    },
  });

  const notFound = is404(error);

  const { mutateAsync, isLoading: isSubmitting } =
    useCustomMutation<CreatorProfile>();

  const submit = async (
    questionnaire: OnboardingQuestionnaire
  ): Promise<CreatorProfile> => {
    const values: CompleteOnboardingQuestionnaireRequest = { questionnaire };
    const { data: result } = await mutateAsync({
      url: baseUrl,
      method: "post",
      values,
      errorNotification: false,
    });

    await queryClient.invalidateQueries({
      predicate: (query) =>
        JSON.stringify(query.queryKey).includes("creator-profiles"),
    });

    return result;
  };

  return {
    template: notFound ? undefined : data?.data,
    isLoading: !!organizationId && isLoading,
    isError: isError && !notFound,
    notFound,
    submit,
    isSubmitting,
  };
};
